/**
 * ChatPanel — context-aware AI assistant side panel.
 *
 * Provides a conversational interface backed by Claude Opus with tool_use.
 * The agent can read from any computed analysis data to answer questions
 * about the deal. Context-aware: knows which screen the user is viewing
 * and prioritizes relevant data accordingly.
 *
 * Renders as a slide-out panel on the right side of the screen,
 * toggled by a floating button.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { backendPy } from "System/system";
import { runAll } from "Redux/data/actions";
import { saveChat, loadChat, generateTitle, clearPendingOpen } from "Redux/chats/actions";
import { getCalcSteps } from "Config/calcParamsConfig";
import RunProgressModal from "Components/RunProgressModal";
import ScenarioProposalCard from "./ScenarioProposalCard";
import CompareDelta from "./CompareDelta";
import Markdown from "./Markdown";
import ChatChart from "./ChatChart";

// Panel width — responsive but capped, so it's comfortable on wide screens and
// doesn't overflow narrow ones. Used by the panel, the toggle button offset, and
// the click-outside overlay so they stay aligned.
const PANEL_W = "min(600px, 94vw)";

const ChatPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Persistence: a stored chat = its messages + the scenarios it created.
  const [chatId, setChatId] = useState(null);
  const [chatTitle, setChatTitle] = useState("");
  const [chatScenarios, setChatScenarios] = useState([]); // {id,name,spec,createdAt}
  const [reactivate, setReactivate] = useState([]); // stale scenarios needing a re-run (transient)
  const createdAtRef = useRef(null);
  const saveTimer = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  const { leaf } = useSelector((state) => state.nav);
  const project = useSelector((state) => state.project);
  const runStatus = useSelector((state) => state.data.runStatus);
  const pendingOpenId = useSelector((state) => state.chats.pendingOpenId);
  const steps = getCalcSteps(project.projectType);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userMessage }]);
    setLoading(true);

    try {
      // Build the conversation history for Claude (only user/assistant, not system)
      const history = [...messages, { role: "user", text: userMessage }];
      const claudeMessages = history.map((m) => ({
        role: m.role,
        content: m.text,
      }));

      const context = {
        screen: leaf,
        referenceDate: project.parameters?.referenceDate || "",
      };

      // Serialize the saved report if available
      const reportJson = project.savedReport?.data
        ? JSON.stringify(project.savedReport.data)
        : "";

      const result = await backendPy("chat", "chat", {
        projectid: project.id,
        datapath: project.datapath,
        messages: JSON.stringify(claudeMessages),
        context: JSON.stringify(context),
        report_json: reportJson,
      });

      if (result.data?.response) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: result.data.response,
            action: result.data.action || null, // scenario proposal, if any
            charts: result.data.charts || null, // embedded charts, if any
          },
        ]);
      } else if (result.data?.error) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", text: `Error: ${result.data.error}` },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: `Error: ${err.message}` },
      ]);
    }

    setLoading(false);
  };

  // Mark a proposal message's action consumed so it can't be double-fired.
  const consumeAction = (idx) =>
    setMessages((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, action: { ...m.action, consumed: true } } : m))
    );

  const push = (msg) => setMessages((prev) => [...prev, msg]);

  // Create/seed the scenario, run the full analysis against it, switch to it, and
  // post the comparison. Driven through Redux runStatus so the progress modal
  // survives the panel being closed. Records the scenario as owned by THIS chat
  // (so it's stored + restored with the conversation).
  const runScenarioSpec = async ({ scenarioId, name, spec, preview }) => {
    try {
      await backendPy("scenario", "create", {
        projectid: project.id,
        scenarioId,
        name,
        specString: JSON.stringify(spec),
      });
      setChatScenarios((prev) =>
        prev.some((s) => s.id === scenarioId) ? prev : [...prev, { id: scenarioId, name, spec, createdAt: Date.now() }]
      );
      dispatch({
        type: "ADD_SCENARIO",
        scenario: { id: scenarioId, name, spec, status: "running", preview },
      });
      dispatch({ type: "SET_RUN_STATUS", runStatus: { scenarioId, step: null, index: 0, total: steps.length, done: false, error: null } });

      const onStep = (step, index, total) =>
        dispatch({ type: "SET_RUN_STATUS", runStatus: { step, index, total } });
      const onDone = () => dispatch({ type: "SET_RUN_STATUS", runStatus: { done: true } });
      const onError = (msg) => dispatch({ type: "SET_RUN_STATUS", runStatus: { error: msg } });

      await dispatch(runAll(scenarioId, onStep, onDone, onError));

      dispatch({ type: "UPDATE_SCENARIO", id: scenarioId, patch: { status: "ready" } });
      dispatch({ type: "SET_ACTIVE_SCENARIO", scenarioId });

      const cmp = await backendPy("scenario", "compare", { projectid: project.id, scenarioId });
      push({ role: "assistant", text: `Done — "${name}" is ready. Here's how it compares to the original:`, compare: cmp.data });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || String(err);
      dispatch({ type: "UPDATE_SCENARIO", id: scenarioId, patch: { status: "failed" } });
      push({ role: "assistant", text: `The scenario run failed: ${msg}` });
    }
  };

  const runScenario = async (idx, action) => {
    consumeAction(idx);
    await runScenarioSpec(action);
  };

  const dismissAction = (idx) => consumeAction(idx);

  // Re-run a stale (data-cleared) scenario surfaced when a chat was reopened.
  const reactivateScenario = async (s) => {
    setReactivate((prev) => prev.filter((x) => x.id !== s.id));
    await runScenarioSpec({ scenarioId: s.id, name: s.name, spec: s.spec, preview: {} });
  };

  // Auto-save (debounced) once there's a real exchange. First save generates an
  // LLM title. The chat + the scenarios it owns are persisted as a unit.
  const hasExchange =
    messages.some((m) => m.role === "user") && messages.some((m) => m.role === "assistant");
  useEffect(() => {
    if (!hasExchange || !project.id) return;
    if (!chatId) {
      const id =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      createdAtRef.current = Date.now();
      setChatId(id);
      return; // next run persists
    }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      let title = chatTitle;
      if (!title) {
        title = await generateTitle(
          messages.slice(0, 4).map((m) => ({ role: m.role, content: m.text }))
        );
        if (title) setChatTitle(title);
      }
      const preview = messages.find((m) => m.role === "user")?.text?.slice(0, 140) || "";
      dispatch(
        saveChat({
          chatId,
          title: title || "Deal analysis chat",
          preview,
          messages,
          scenarios: chatScenarios,
          createdAt: createdAtRef.current,
        })
      );
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [messages, chatScenarios, chatId, chatTitle, hasExchange, project.id, dispatch]);

  // Start a fresh conversation (the current one is already auto-saved).
  const newChat = () => {
    clearTimeout(saveTimer.current);
    setMessages([]);
    setChatId(null);
    setChatTitle("");
    setChatScenarios([]);
    setReactivate([]);
    createdAtRef.current = null;
    inputRef.current?.focus();
  };

  // Load a stored chat and reactivate its scenarios (reuse on-disk data if it's
  // still there; otherwise surface a re-run prompt for the stale ones).
  const openStoredChat = useCallback(async (id) => {
    const doc = await loadChat(id);
    if (!doc || !doc.chatId) return;
    clearTimeout(saveTimer.current);
    setChatId(doc.chatId);
    createdAtRef.current = doc.createdAt || Date.now();
    setChatTitle(doc.title || "");
    setChatScenarios(doc.scenarios || []);
    setMessages(doc.messages || []);
    setReactivate([]);
    setIsOpen(true);

    const scenarios = doc.scenarios || [];
    if (scenarios.length) {
      let diskIds = new Set();
      try {
        const r = await backendPy("scenario", "list", { projectid: project.id });
        diskIds = new Set((r.data?.scenarios || []).map((s) => s.scenarioId));
      } catch (e) {
        /* treat as none on disk */
      }
      const stale = [];
      scenarios.forEach((s) => {
        const exists = diskIds.has(s.id);
        dispatch({
          type: "ADD_SCENARIO",
          scenario: { id: s.id, name: s.name, spec: s.spec, status: exists ? "ready" : "stale", createdAt: s.createdAt },
        });
        if (!exists) stale.push(s);
      });
      setReactivate(stale);
    }
  }, [dispatch, project.id]);

  // History modal picked a chat → load & open it.
  useEffect(() => {
    if (pendingOpenId) {
      openStoredChat(pendingOpenId);
      dispatch(clearPendingOpen());
    }
  }, [pendingOpenId, openStoredChat, dispatch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Screen name for display
  const screenNames = {
    aging: "Aging", rollforward: "Rollforward", delinquencies: "Delinquencies",
    dilution: "Dilution", buyerparent: "Accounts Receivable",
    concentrationlimits: "Concentration Limits", nerb: "NERB",
    report: "Report", buyerratings: "Buyer Ratings",
    ratingscale: "Rating Scale", parameters: "Parameters",
    documenttypes: "Document Types", canonicals: "Column Mapping",
    downloads: "Downloads", master: "Master Data",
  };

  return (
    <>
      {/* Scenario run progress — driven by Redux so it survives closing the panel */}
      {runStatus && (
        <RunProgressModal
          steps={steps}
          currentStep={runStatus.step}
          stepIndex={runStatus.index}
          isDone={runStatus.done}
          error={runStatus.error}
          title="Running Scenario"
          onClose={() => dispatch({ type: "CLEAR_RUN_STATUS" })}
        />
      )}

      {/* Floating toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "fixed",
          bottom: "24px",
          right: isOpen ? `calc(${PANEL_W} + 12px)` : "24px",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
          color: "#fff",
          border: "none",
          boxShadow: "0 4px 16px rgba(67, 0, 152, 0.35)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.2rem",
          zIndex: 1001,
          transition: "right 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <i className={isOpen ? "fa-solid fa-chevron-right" : "fa-duotone fa-comments"} />
      </button>

      {/* Click-outside overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: `calc(100vw - ${PANEL_W})`,
            height: "100vh",
            zIndex: 998,
          }}
        />
      )}

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: isOpen ? PANEL_W : "0px",
          height: "100vh",
          backgroundColor: "var(--ev-white)",
          borderLeft: isOpen ? "1px solid var(--ev-border)" : "none",
          boxShadow: isOpen ? "-4px 0 24px rgba(0,0,0,0.15)" : "none",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "0.75rem 1rem",
            borderBottom: "1px solid var(--ev-border)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexShrink: 0,
            background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
            color: "#fff",
          }}
        >
          <i className="fa-duotone fa-brain" style={{ fontSize: "1rem" }} />
          <span
            style={{ fontWeight: 700, fontSize: "0.85rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={chatTitle || "Deal Analyst"}
          >
            {chatTitle || "Deal Analyst"}
          </span>
          {messages.length > 0 && (
            <button
              onClick={newChat}
              title="New chat"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "0.7rem",
                padding: "0.2em 0.5em",
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
              }}
            >
              <i className="fa-solid fa-pen-to-square" /> New
            </button>
          )}
          <span
            style={{
              fontSize: "0.65rem",
              opacity: 0.8,
              backgroundColor: "rgba(255,255,255,0.15)",
              padding: "0.15em 0.5em",
              borderRadius: "4px",
            }}
          >
            {screenNames[leaf] || leaf}
          </span>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0.75rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.6rem",
          }}
        >
          {messages.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem 1rem",
                color: "var(--ev-text-muted)",
                fontSize: "0.82rem",
                lineHeight: 1.6,
              }}
            >
              <i
                className="fa-duotone fa-wand-magic-sparkles"
                style={{ fontSize: "2rem", marginBottom: "0.75rem", display: "block", opacity: 0.4 }}
              />
              Ask me anything about this deal. I can read all the analysis data
              to answer your questions.
              <div style={{ marginTop: "0.75rem", textAlign: "left" }}>
                <div style={{ fontWeight: 600, marginBottom: "0.3rem", color: "var(--ev-text)" }}>
                  Try:
                </div>
                {[
                  "What are the top risk factors?",
                  "Which buyers are closest to their concentration limits?",
                  "How has the dilution ratio trended over the last year?",
                  "Why is the eligibility rate below 70%?",
                ].map((q, i) => (
                  <div
                    key={i}
                    onClick={() => {
                      setInput(q);
                      inputRef.current?.focus();
                    }}
                    style={{
                      padding: "0.35rem 0.5rem",
                      borderRadius: "6px",
                      backgroundColor: "var(--ev-surface)",
                      marginBottom: "0.3rem",
                      cursor: "pointer",
                      fontSize: "0.78rem",
                      color: "var(--ev-text)",
                    }}
                  >
                    {q}
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className="ev-chat-msg" style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    maxWidth: msg.role === "user" ? "85%" : "97%",
                    padding: "0.6rem 0.8rem",
                    borderRadius:
                      msg.role === "user"
                        ? "12px 12px 2px 12px"
                        : "4px 12px 12px 4px",
                    backgroundColor:
                      msg.role === "user" ? "#79589F" : "var(--ev-surface)",
                    borderLeft: msg.role === "assistant" ? "3px solid #79589F" : "none",
                    color: msg.role === "user" ? "#fff" : "var(--ev-text)",
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                    wordBreak: "break-word",
                  }}
                >
                  {msg.role === "assistant" ? (
                    <Markdown text={msg.text} />
                  ) : (
                    <span style={{ whiteSpace: "pre-wrap" }}>{msg.text}</span>
                  )}
                </div>
              </div>

              {msg.action?.type === "run_scenario" && (
                <ScenarioProposalCard
                  action={msg.action}
                  disabled={runStatus && !runStatus.done && !runStatus.error}
                  onRun={() => runScenario(i, msg.action)}
                  onDismiss={() => dismissAction(i)}
                />
              )}

              {msg.charts?.map((chart, ci) => <ChatChart key={ci} chart={chart} />)}

              {msg.compare && <CompareDelta compare={msg.compare} />}
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  padding: "0.6rem 0.8rem",
                  borderRadius: "12px 12px 12px 2px",
                  backgroundColor: "var(--ev-surface)",
                  color: "var(--ev-text-muted)",
                  fontSize: "0.82rem",
                }}
              >
                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: "0.4rem" }} />
                Analyzing...
              </div>
            </div>
          )}

          {/* Stale scenarios from a reopened chat — offer a re-run */}
          {reactivate.map((s) => (
            <div key={s.id} style={{ border: "1px solid var(--ev-warning)", borderRadius: "8px", padding: "0.6rem 0.7rem", fontSize: "0.78rem", backgroundColor: "var(--ev-notification-warning-bg)" }}>
              <div style={{ color: "var(--ev-text)", marginBottom: "0.4rem" }}>
                <i className="fa-duotone fa-triangle-exclamation" style={{ marginRight: "0.35rem", color: "var(--ev-warning)" }} />
                Scenario <strong>{s.name}</strong> from this chat needs to be re-run — its computed data was cleared.
              </div>
              <button
                className="button is-small"
                onClick={() => reactivateScenario(s)}
                disabled={runStatus && !runStatus.done && !runStatus.error}
                style={{ backgroundColor: "#79589F", color: "#fff", border: "none", fontWeight: 700, borderRadius: "6px" }}
              >
                <i className="fa-solid fa-rotate-right" style={{ marginRight: "0.35rem" }} /> Re-run scenario
              </button>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div
          style={{
            padding: "0.6rem",
            borderTop: "1px solid var(--ev-border)",
            display: "flex",
            gap: "0.4rem",
            flexShrink: 0,
          }}
        >
          <input
            ref={inputRef}
            type="text"
            className="input is-small"
            placeholder="Ask about this deal..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            style={{ flex: 1, borderRadius: "8px" }}
          />
          <button
            className="button is-small"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{
              borderRadius: "8px",
              backgroundColor: "#79589F",
              color: "#fff",
              border: "none",
              fontWeight: 700,
            }}
          >
            <i className="fa-solid fa-paper-plane" />
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatPanel;
